import 'vuetify/styles';
import 'vuetify/_settings.scss';
import type { VuetifyOptions } from 'vuetify';
import { createVuetify } from 'vuetify';
import Color from 'color';
import { colorBrighter, generateThemeColorList } from '~/app-modules/core/utils/helpers';

import { VSkeletonLoader } from 'vuetify/lib/components/index.mjs';

// иконки
import ArrowDown from '~/app-modules/core/components/icons/arrow-down.vue';
import Logo from '~/app-modules/core/components/icons/logo.vue';
import Logout from '~/app-modules/core/components/icons/logout.vue';
import ArrowLeft from '~/app-modules/core/components/icons/arrow-left.vue';
import Check from '~/app-modules/core/components/icons/check.vue';
import CalendarEdit from '~/app-modules/core/components/icons/calendar-edit.vue';

// Цвета дизайна
const mainColors = {
  beton: Color.rgb(50, 50, 60),
  blue: Color.rgb(35, 160, 225),
  green: Color.rgb(60, 145, 115),
  red: Color.rgb(205, 85, 100),
  yellow: Color.rgb(242, 176, 73),
  yellowContrast: Color('#ea9b34'),
};

// Конфиг vuetify
const vuetifyConfig: VuetifyOptions = {
  ssr: true,
  defaults: {
    VAlert: { variant: 'tonal', VIcon: { size: '12' } },
    VBtn: { rounded: true },
    VCard: {
      rounded: 'lg',
      elevation: 12,
      border: true,
      VCardTitle: { class: 'pa-2' },
      VCardText: { class: 'pa-2' },
    },
    VCarousel: {
      VBtn: { rounded: undefined },
    },
    VCheckbox: {
      ripple: false,
      falseIcon: '',
      trueIcon: '$check',
    },
    VChip: {
      closeIcon: '$close',
    },
    VTextField: {
      rounded: true,
      variant: 'outlined',
      persistentPlaceholder: true,
      hideDetails: 'auto',
    },
    VTextarea: {
      rounded: true,
      variant: 'outlined',
      persistentPlaceholder: true,
      hideDetails: 'auto',
      autoGrow: false,
      noResize: true,
      rows: 3,
    },
    VAutocomplete: {
      rounded: true,
      variant: 'outlined',
      persistentPlaceholder: true,
      hideDetails: 'auto',
      clearable: true,
      noDataText: 'Нет данных',
    },
    VSelect: {
      rounded: true,
      variant: 'outlined',
      persistentPlaceholder: true,
      hideDetails: 'auto',
      clearable: true,
      noDataText: 'Нет данных',
    },
    VCombobox: {
      rounded: true,
      variant: 'outlined',
      persistentPlaceholder: true,
      hideDetails: 'auto',
      clearable: true,
      noDataText: 'Нет данных',
    },
    VSwitch: {
      flat: true,
      inset: true,
      color: 'blue',
      hideDetails: true,
      VLabel: {
        class: 'text-beton ml-1',
      },
    },
    VMenu: {
      offset: '4',
    },
    VList: {
      rounded: true,
      elevation: '10',
      border: 0,
    },
    VListItem: {
      rounded: true,
      VListItemTitle: { class: 'text-h4 text-beton64' },
      VIcon: { class: 'text-beton64 opacity-100' },
      VCheckboxBtn: {
        VIcon: { size: '12', falseIcon: '' },
        ripple: false,
        falseIcon: '',
      },
    },
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        colors: {
          primary: mainColors.blue.hex(),
          secondary: mainColors.yellow.hex(),
          error: mainColors.red.hex(),
          warning: mainColors.yellowContrast.hex(),
          success: mainColors.green.hex(),
          'on-surface': mainColors.beton.hex(), // цвет текста на поверхностях
          'on-primary': colorBrighter(mainColors.beton, 0.03), // цвет текста на поверхностях
          link: mainColors.blue.hex(),
          //
          // Сгенерированные цвета
          ...generateThemeColorList('beton', Color.rgb(50, 50, 60)),
          ...generateThemeColorList('blue', mainColors.blue),
          ...generateThemeColorList('green', mainColors.green),
          ...generateThemeColorList('red', mainColors.red),
          ...generateThemeColorList('yellow', mainColors.yellow),
          'yellow-contrast': mainColors.yellowContrast.hex(),
        },
        variables: {
          'border-color': '#32323C',
          'border-opacity': 0.2,
          'border-hover-opacity': 0.64,
          'default-emphasis-opacity': 0.84, // прозрачность всех цветов (дополнительная переменная)
          'ripple-emphasis-opacity': 0.12,
          'high-emphasis-opacity': 0.8, // влияет на v-ripple
          // 'medium-emphasis-opacity': 0.6, // влияет на opacity иконок в списках
          'disabled-opacity': 0.6,
          'idle-opacity': 0.04,
          'hover-opacity': 0.1,
          'focus-opacity': 0.1,
          'selected-opacity': 0.1,
          'activated-opacity': 0.1, // активное состояние кнопок и карточек
          'pressed-opacity': 0.1,
          'dragged-opacity': 0.1,
        },
      },
    },
  },
  components: {
    VSkeletonLoader,
  },
  icons: {
    aliases: {
      'arrow-down': ArrowDown,
      logout: Logout,
      logo: Logo,
      'arrow-left': ArrowLeft,
      check: Check,
      'calendar-edit': CalendarEdit,

      // стандартные иконки vuetify
      // TODO: добавить
    },
  },
  display: {
    mobileBreakpoint: 'xs',
    thresholds: {
      xs: 0,
      sm: 768,
      md: 1024,
      lg: 1280,
      xl: 1920,
      xxl: 9999,
    },
  },
};

export const vuetify = createVuetify(vuetifyConfig);

export default defineNuxtPlugin({
  name: 'vuetify',
  parallel: false,
  enforce: 'pre', // pre|default|post
  dependsOn: ['userAgent'],
  async setup(nuxt) {
    // Плагин определяет тип устройства телефон|планшет|десктоп,
    // на основе этого определяется ширина фрейма для рендера на ssr
    const userAgentDisplay = useNuxtApp().$userAgentBreakpoint.value;
    // noinspection TypeScriptValidateTypes
    const clientWidth = {
      xs: 375,
      md: 1024,
      lg: 1280,
    }[userAgentDisplay];

    const vuetifyPlugin = createVuetify({ ...vuetifyConfig, ssr: { clientWidth } });
    nuxt.vueApp.use(vuetifyPlugin);
    nuxt.provide('vuetify', vuetifyPlugin);
  },
});

interface PluginsInjections {
  $vuetify: typeof vuetify;
}

declare module '#app' {
  interface NuxtApp extends PluginsInjections {}
}
